import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Alert, Flex, Input, Modal, Spinner, Text } from '@/lib/v2/components';

import { ButtonActions } from '@/src/modules/CampaignsModule/components/CampaignsModal/CreateEditCampaigns/components/ButtonActions';

import {
  atomLoadingDetail,
  atomRefetchAllData,
  atomSelectedActionsRowsWithID,
  atomSelectedRowActionName,
} from '@/modules/CampaignsModule/atoms/campaignAtom';
import { atomToggleDuplicateActionsModal } from '@/modules/CampaignsModule/atoms/modalsCampaignAtom';
import { useCampaignActionsStateContext } from '@/modules/CampaignsModule/contexts/CampaignActionsContext';
import { useCampaignActions } from '@/modules/CampaignsModule/hooks/useCampaignActions';
import { EMaxMinLength, IFormInput } from '@/modules/CampaignsModule/interfaces/Campaigns';

const TRANSLATE_MODAL = 'CAMPAIGNS_MAIN.CREATE_EDIT_CAMPAIGNS';
const TRANSLATE_MODAL_INPUTS = `${TRANSLATE_MODAL}.INPUTS_FORM`;
const TRANSLATE_MODAL_DUPLICATE = 'CAMPAIGN_ACTIONS_MAIN.DUPLICATE_ACTIONS';

interface IDuplicateActionsProps {
  onClose: (value?: boolean) => void;
}

const DuplicateActions = ({ onClose }: IDuplicateActionsProps) => {
  const { t } = useTranslation();
  const { id } = useParams();

  const [toggleDuplicateActionsModal] = useAtom(atomToggleDuplicateActionsModal);
  const { filterBy, orderBy, pageIndex, pageSize } = useCampaignActionsStateContext();
  const { duplicateAction, loading } = useCampaignActions({
    campaignID: Number(id),
    campaignActionsStateContext: { filterBy, orderBy, pageIndex, pageSize },
  });
  const setRefetchAllData = useSetAtom(atomRefetchAllData);

  const { control, handleSubmit, setValue } = useForm<IFormInput>();

  const loadingDetail = useAtomValue(atomLoadingDetail);

  const actionId = useAtomValue(atomSelectedActionsRowsWithID);
  const actionName = useAtomValue(atomSelectedRowActionName);

  // eslint-disable-next-line regexp/no-obscure-range
  const REGEX_INPUT = /^[\dA-Za-zÀ-ÖØ-öø-ÿ\s\-,'.!¡¿?()\\"&$%]+$/u;

  const handleCloseModal = useCallback(() => onClose(), [onClose]);

  const VALIDATION_RULES = {
    name: {
      pattern: {
        value: REGEX_INPUT,
        message: t(`${TRANSLATE_MODAL}.INPUTS_ERROR.pattern`),
      },
      required: {
        value: true,
        message: t(`${TRANSLATE_MODAL}.INPUTS_ERROR.required`),
      },
      minLength: {
        value: EMaxMinLength.MIN_LENGTH,
        message: t(`${TRANSLATE_MODAL}.INPUTS_ERROR.minLength`, {
          name: t(`${TRANSLATE_MODAL_INPUTS}.name.label`).toLowerCase(),
          min: EMaxMinLength.MIN_LENGTH,
        }),
      },
      maxLength: {
        value: EMaxMinLength.NAME_MAX,
        message: t(`${TRANSLATE_MODAL}.INPUTS_ERROR.maxLength`, {
          name: t(`${TRANSLATE_MODAL_INPUTS}.name.label`).toLowerCase(),
          max: EMaxMinLength.NAME_MAX,
        }),
      },
    },
  };

  const handleSubmitData = useCallback(
    async (formData: IFormInput) => {
      await duplicateAction({
        name: formData.name,
        actionId: Number(actionId),
      });
      handleCloseModal();
      setRefetchAllData(true);
    },
    [actionId, duplicateAction, handleCloseModal, setRefetchAllData]
  );

  useEffect(() => {
    setValue('name', `${String(actionName)} ${t(`${TRANSLATE_MODAL_DUPLICATE}.copy`)}`);
  }, [setValue, actionName]);

  return (
    <Modal open={toggleDuplicateActionsModal} onClose={handleCloseModal}>
      <div className="flex flex-col gap-2 divide-y">
        <header>
          <Text as="h3" fontWeight="medium" variant="sub-headline">
            {t(`${TRANSLATE_MODAL_DUPLICATE}.title`)}
          </Text>
        </header>
        <section className="flex w-full flex-col pt-6">
          {!loadingDetail ? (
            <form
              autoComplete="off"
              id="createEditCampaigns"
              onSubmit={handleSubmit(handleSubmitData)}
            >
              <Flex column withGap alignment="spaceEvenly" gapSize="small">
                <Input
                  isRequired
                  control={control}
                  id="name"
                  label={t(`${TRANSLATE_MODAL_INPUTS}.name.label`)}
                  name="name"
                  placeHolder={t(`${TRANSLATE_MODAL_INPUTS}.name.placeholder`)}
                  rules={VALIDATION_RULES.name}
                  type="text"
                />
              </Flex>
              <div className="mt-4">
                <Alert description={t(`${TRANSLATE_MODAL_DUPLICATE}.description`)} type="info" />
              </div>
              <ButtonActions
                isLoading={loading}
                textCancelButton={t(`${TRANSLATE_MODAL_DUPLICATE}.cancelButton`)}
                textSubmitButton={t(`${TRANSLATE_MODAL_DUPLICATE}.successButton`)}
                onClose={handleCloseModal}
              />
            </form>
          ) : (
            <Flex alignment="center" className="min-h-[122px]">
              <Spinner withoutOverlay />
            </Flex>
          )}
        </section>
      </div>
    </Modal>
  );
};

export default DuplicateActions;
